/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { IDocumentError } from "@adapptio/lib-blueprint";

import { IFieldValidationError } from "./FieldTypes";
import { CodeEditor, ICodeEditorProps } from "@adapptio/lib-editor-ui";
import { Icon, ICON_SIZE } from "../Icon";
import { FieldWrapper } from "./FieldWrapper";
import { FieldValidationErrors } from "./FieldValidationErrors";
import { InputButton } from "../InputButton";

export interface IFieldCodeProps {
	id?: string;
	label?: string;
	value: string;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	parseErrors?: IDocumentError[];
	resultData?: unknown;
	className?: string;
	onChange?: (value: string) => void;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	grow?: boolean;
	inputRef?: React.MutableRefObject<HTMLInputElement>;
	language?: ICodeEditorProps["language"];
	debounceTimeout?: number;
	canExpand?: boolean;
	isExpanded?: boolean;
	onExpand?: () => void;
}

export const FieldCode: React.FC<IFieldCodeProps> = ({
	className,
	value,
	readOnly,
	onChange,
	validationErrors,
	parseErrors,
	grow,
	language,
	debounceTimeout,
	canExpand,
	isExpanded,
	onExpand
}) => {
	const wrapperRef = useRef();

	const baseClass = BEM("edt-field-control");

	const handleChange = useCallback<ICodeEditorProps["onChange"]>(
		(value) => {
			if (isFunction(onChange)) {
				onChange(value || "");
			}
		},
		[ onChange ]
	);

	const handleEditorExpandClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
		if (canExpand && isFunction(onExpand)) {
			onExpand();
		}
	}, [ canExpand, onExpand ]);

	const commonProps: Partial<ICodeEditorProps> = {
		value,
		onChange: handleChange,
		language,
		parseErrors,
		readOnly,
		debounceTimeout
	};

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldWrapper type="code" grow={grow} wrapperRef={wrapperRef}>
			<div className={baseClass.toClassName()}>
				<div className={baseClass.element("editor").toClassName()}>
					<CodeEditor singleLine {...commonProps} />
				</div>
				<FieldValidationErrors errors={validationErrors} />
				{canExpand && (
					<InputButton
						className={baseClass.element("editor-popup-trigger").toClassName()}
						onClick={handleEditorExpandClick}
						tabIndex={-1}
						active={isExpanded}
					>
						<Icon
							className={baseClass.element("editor-popup-trigger-icon").toClassName()}
							name="mdi/open-in-new"
							// @todo Boris USE ONLY CUSTOM ICON SIZE and style it in CSS using mixin!
							size={ICON_SIZE.XSMALL}
						/>
					</InputButton>
				)}
			</div>
		</FieldWrapper>
	);
};
